import {
    Platform,
    type View
} from "react-native";
import type {
    ComboboxControlElementConstantType,
    ComboboxRenderedItemsConstantType,
    ComboboxVisibleCountConstantType,
    ComboboxLeavingItemsConstantType,
    ComboboxLeavingExitConstantType,
    ComboboxResolveConstantType,
    ComboboxEntriesConstantType,
    ComboboxToggleConstantType,
    ComboboxFilterConstantType,
    ComboboxCreateConstantType,
    ComboboxGroupsConstantType,
    ComboboxValueConstantType,
    ComboboxLimitConstantType,
    ComboboxMatchConstantType,
    ComboboxKeysConstantType,
    ComboboxToggleResultType,
    ComboboxMoveConstantType,
    ComboboxFoldConstantType,
    ComboboxSelectedItem,
    ComboboxRenderedItem,
    ComboboxLeavingItem,
    ComboboxMatchType,
    ComboboxValue,
    ComboboxEntry,
    ComboboxGroup
} from "./type";
import {
    getSelectionGroups
} from "../../helpers/selection";

export const COMBOBOX_CREATE_KEY = "__combobox_create__";

export const COMBOBOX_PAGE_ROWS = 8;

const COMBOBOX_COMBINING_MARKS = /[̀-ͯ]/g;

const COMBOBOX_DOTLESS_I = /ı/g;

const getIsView = (value: unknown): value is View => {
    if(typeof HTMLElement !== "undefined" && value instanceof HTMLElement) {
        return true;
    }

    return typeof value === "object" && value !== null && "measureInWindow" in value;
};

export const getComboboxControlElement = ({
    node
}: ComboboxControlElementConstantType): View | null => {
    if(Platform.OS !== "web" || typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return node;
    }

    const parent = node.parentElement;

    return getIsView(parent) ? parent : node;
};

export const getComboboxFold = ({
    locale,
    text
}: ComboboxFoldConstantType): string => {
    return text
        .toLocaleLowerCase(locale)
        .normalize("NFD")
        .replace(COMBOBOX_COMBINING_MARKS, "")
        .replace(COMBOBOX_DOTLESS_I, "i");
};

export const getComboboxMatch = ({
    foldedQuery: foldedQueryProp,
    locale,
    query,
    title
}: ComboboxMatchConstantType): ComboboxMatchType | undefined => {
    const foldedQuery = foldedQueryProp ?? getComboboxFold({
        text: query,
        locale
    });

    if(!foldedQuery.length) {
        return undefined;
    }

    const origins: Array<number> = [];

    let folded = "";

    for(let index = 0; index < title.length; index += 1) {
        const piece = getComboboxFold({
            text: title.charAt(index),
            locale
        });

        folded += piece;

        for(let pieceIndex = 0; pieceIndex < piece.length; pieceIndex += 1) {
            origins.push(index);
        }
    }

    const foldedIndex = folded.indexOf(foldedQuery);

    if(foldedIndex === -1) {
        return undefined;
    }

    const start = origins[foldedIndex];

    const lastOrigin = origins[foldedIndex + foldedQuery.length - 1];

    if(start === undefined || lastOrigin === undefined) {
        return undefined;
    }

    return {
        end: lastOrigin + 1,
        start
    };
};

export const getComboboxEntries = <T>({
    subTitleExtractor,
    groupExtractor,
    titleExtractor,
    keyExtractor,
    items
}: ComboboxEntriesConstantType<T>): Array<ComboboxEntry<T>> => {
    return items.map((item, index) => ({
        sub: subTitleExtractor ? subTitleExtractor(item) : undefined,
        group: groupExtractor ? groupExtractor(item) : undefined,
        title: titleExtractor(item, index),
        key: keyExtractor(item, index),
        isCreate: false,
        item
    }));
};

export const getComboboxFiltered = <T>({
    isLocalFilter,
    entries,
    locale,
    query
}: ComboboxFilterConstantType<T>): Array<ComboboxEntry<T>> => {
    if(!query.length) {
        return entries;
    }

    const filtered: Array<ComboboxEntry<T>> = [];

    const foldedQuery = getComboboxFold({
        text: query,
        locale
    });

    entries.forEach((entry) => {
        const match = getComboboxMatch({
            title: entry.title,
            foldedQuery,
            locale,
            query
        });

        if(isLocalFilter && !match) {
            return;
        }

        filtered.push({
            ...entry,
            match
        });
    });

    return filtered;
};

export const getComboboxCreateEntry = <T>({
    createLabel,
    onCreate,
    entries,
    locale,
    query
}: ComboboxCreateConstantType<T>): ComboboxEntry<T> | undefined => {
    if(!onCreate || !query.length) {
        return undefined;
    }

    const foldedQuery = getComboboxFold({
        text: query,
        locale
    });

    const isExact = entries.some((entry) => getComboboxFold({
        text: entry.title,
        locale
    }) === foldedQuery);

    if(isExact) {
        return undefined;
    }

    return {
        key: COMBOBOX_CREATE_KEY,
        title: createLabel,
        isCreate: true
    };
};

export const getComboboxGroups = <T>({
    entries
}: ComboboxGroupsConstantType<T>): Array<ComboboxGroup<T>> => {
    return getSelectionGroups({
        getHeading: (entry) => entry.isCreate ? undefined : entry.group || undefined,
        getKey: (entry) => entry.isCreate ? COMBOBOX_CREATE_KEY : entry.group ?? "",
        entries
    });
};

export const getComboboxIsLimitReached = ({
    selectedCount,
    isMultiple,
    isSelected,
    maxChoice
}: ComboboxLimitConstantType): boolean => {
    if(!isMultiple || isSelected || maxChoice === undefined || maxChoice <= 0) {
        return false;
    }

    return selectedCount >= maxChoice;
};

export const getComboboxToggle = ({
    isMultiple,
    maxChoice,
    minChoice,
    current,
    item
}: ComboboxToggleConstantType): ComboboxToggleResultType => {
    const isSelected = current.some((selectedItem) => selectedItem.__key === item.__key);

    if(!isMultiple) {
        return {
            isChanged: !isSelected || current.length !== 1,
            items: [
                item
            ]
        };
    }

    if(isSelected) {
        if(minChoice && current.length <= minChoice) {
            return {
                isChanged: false,
                items: current
            };
        }

        return {
            items: current.filter((selectedItem) => selectedItem.__key !== item.__key),
            isChanged: true
        };
    }

    if(getComboboxIsLimitReached({
        selectedCount: current.length,
        isMultiple,
        isSelected,
        maxChoice
    })) {
        return {
            isChanged: false,
            items: current
        };
    }

    return {
        isChanged: true,
        items: [
            ...current,
            item
        ]
    };
};

export const getComboboxMove = ({
    activeKey,
    delta,
    keys
}: ComboboxMoveConstantType): string | undefined => {
    if(!keys.length) {
        return undefined;
    }

    const index = activeKey === undefined ? -1 : keys.indexOf(activeKey);

    if(index === -1) {
        return delta > 0 ? keys[0] : keys[keys.length - 1];
    }

    return keys[Math.min(keys.length - 1, Math.max(0, index + delta))];
};

export const getComboboxVisibleCount = ({
    available,
    plusWidth,
    widths,
    gap
}: ComboboxVisibleCountConstantType): number => {
    const total = widths.length;

    if(!total || available <= 0 || widths.some((width) => width === undefined)) {
        return total;
    }

    let used = 0;

    let count = 0;

    for(let index = 0; index < total; index += 1) {
        const next = used + (count ? gap : 0) + (widths[index] ?? 0);

        const reserve = total - index - 1 > 0 ? gap + plusWidth : 0;

        if(next + reserve > available) {
            break;
        }

        used = next;

        count = index + 1;
    }

    return Math.max(1, count);
};

export const getComboboxRenderedItems = ({
    selectedItems,
    leavingItems,
    visibleCount
}: ComboboxRenderedItemsConstantType): Array<ComboboxRenderedItem> => {
    const renderedItems: Array<ComboboxRenderedItem> = selectedItems.slice(0, visibleCount).map((item) => ({
        isLeaving: false,
        item
    }));

    leavingItems.forEach((leavingItem) => {
        renderedItems.splice(Math.min(leavingItem.index, renderedItems.length), 0, {
            item: leavingItem.item,
            isLeaving: true
        });
    });

    return renderedItems;
};

export const getComboboxLeavingItems = ({
    renderedItems,
    leavingItems,
    nextItems
}: ComboboxLeavingItemsConstantType): Array<ComboboxLeavingItem> => {
    const nextKeys = new Set(nextItems.map((item) => item.__key));

    const leavingKeys = new Set(leavingItems.map((leavingItem) => leavingItem.item.__key));

    const removedItems: Array<ComboboxLeavingItem> = [];

    renderedItems.forEach((renderedItem, index) => {
        if(renderedItem.isLeaving || nextKeys.has(renderedItem.item.__key) || leavingKeys.has(renderedItem.item.__key)) {
            return;
        }

        removedItems.push({
            item: renderedItem.item,
            index
        });
    });

    return [
        ...leavingItems.filter((leavingItem) => !nextKeys.has(leavingItem.item.__key)),
        ...removedItems
    ];
};

export const getComboboxLeavingAfterExit = ({
    leavingItems,
    key
}: ComboboxLeavingExitConstantType): Array<ComboboxLeavingItem> => {
    const exitedItem = leavingItems.find((leavingItem) => leavingItem.item.__key === key);

    if(!exitedItem) {
        return leavingItems;
    }

    return leavingItems
        .filter((leavingItem) => leavingItem !== exitedItem)
        .map((leavingItem) => leavingItem.index > exitedItem.index ? {
            ...leavingItem,
            index: leavingItem.index - 1
        } : leavingItem);
};

export const getComboboxKeys = ({
    isMultiple,
    value
}: ComboboxKeysConstantType): Array<string> => {
    if(value === undefined || value === "") {
        return [];
    }

    if(typeof value === "string") {
        return [
            value
        ];
    }

    return isMultiple ? value : value.slice(0, 1);
};

export const getComboboxValue = ({
    isMultiple,
    items
}: ComboboxValueConstantType): ComboboxValue => {
    const keys = items.map((item) => item.__key);

    return isMultiple ? keys : keys[0] ?? "";
};

export const getComboboxResolvedItems = <T>({
    current,
    entries,
    known,
    keys
}: ComboboxResolveConstantType<T>): Array<ComboboxSelectedItem> => {
    const titles = new Map(known);

    current.forEach((item) => titles.set(item.__key, item.__title));

    entries.forEach((entry) => titles.set(entry.key, entry.title));

    return keys.map((key) => ({
        __title: titles.get(key) ?? key,
        __key: key
    }));
};
